import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { UserCircle, Save } from "lucide-react";

export default function Profile() {
  const { refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    job_title: "",
    notification_email: "",
    email_notifications_enabled: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me").then(({ data }) => {
      setMe(data);
      setForm({
        full_name: data.full_name || "",
        job_title: data.job_title || "",
        notification_email: data.notification_email || "",
        email_notifications_enabled: !!data.email_notifications_enabled,
      });
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me", form);
      toast.success("Profile updated");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full h-9 px-3 rounded-md border text-sm mt-1";
  const border = { borderColor: "var(--cw-border)" };

  if (!me) return <div className="p-8 text-sm text-[var(--cw-muted)]">Loading…</div>;

  return (
    <div className="p-8 max-w-xl">
      <div className="cw-caption uppercase tracking-widest">Account</div>
      <h1 className="cw-h1 mt-1 inline-flex items-center gap-2">
        <UserCircle size={22} /> My Profile
      </h1>

      <div className="mt-6 cw-card p-5 space-y-4">
        <div>
          <div className="cw-caption">Email (login)</div>
          <div className="mt-1 text-sm text-[var(--cw-muted)]">{me.email}</div>
        </div>
        <div>
          <div className="cw-caption">Role</div>
          <div className="mt-1 text-sm text-[var(--cw-muted)]">{me.role}</div>
        </div>
        <div>
          <div className="cw-caption">Full name</div>
          <input
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className={inp}
            style={border}
            data-testid="profile-fullname"
          />
        </div>
        <div>
          <div className="cw-caption">Job title</div>
          <input
            value={form.job_title}
            onChange={(e) => setForm({ ...form, job_title: e.target.value })}
            className={inp}
            style={border}
            data-testid="profile-jobtitle"
          />
        </div>
        <div>
          <div className="cw-caption">Notification email (optional — future email delivery)</div>
          <input
            type="email"
            value={form.notification_email}
            onChange={(e) => setForm({ ...form, notification_email: e.target.value })}
            className={inp}
            style={border}
            data-testid="profile-notif-email"
            placeholder={me.email}
          />
          <div className="text-[10px] text-[var(--cw-muted)] mt-1">
            Kept for future use. This build sends in-app notifications only.
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.email_notifications_enabled}
            onChange={(e) => setForm({ ...form, email_notifications_enabled: e.target.checked })}
            data-testid="profile-email-toggle"
          />
          Enable email notifications when they become available
        </label>

        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="cw-btn-primary text-sm inline-flex items-center gap-1"
            data-testid="profile-save"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save Profile"}
          </button>
        </div>
      </div>
    </div>
  );
}
